/*
 * A conversation just opened is drawn once its emails have their height (frontend performance plan,
 * W6, F16). Each email is a frame sized to what it renders, and a frame can only be measured once
 * its document has loaded, a few frames after the thread is drawn. Shown at once, every email grew
 * then, and the newest message and the reply agent's card under them were pushed down, often out of
 * view: 0.08 of layout shift on a desktop and 0.18 on a phone in the lab, each time a conversation
 * opened slower than half a second (`tests/browser/vitals-lab.ts`).
 *
 * So the thread's pane holds what it draws hidden, keeping its place, until every email drawn with
 * it has loaded and been measured, or `SETTLE_MS` has passed, whichever is first; a frame that never
 * loads cannot keep the conversation hidden. What appears is not a shift; what moves while visible
 * is. Settling happens once per conversation: an email arriving later, or a picture loading, shows
 * as it lands.
 */
import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";

/** The longest a conversation's emails are waited for before it is shown regardless. */
export const SETTLE_MS = 400;

/** One opened conversation's emails, counted until each has loaded and been measured. */
export class FrameSettle {
  #pending = 0;
  #started = false;
  #open = true;
  readonly #settled: () => void;
  readonly #listeners = new Set<() => void>();

  constructor(settled: () => void) {
    this.#settled = settled;
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };

  readonly isSettled = (): boolean => !this.#open;

  /** An email drawn with the conversation; the returned callback says it has its height. */
  add(): () => void {
    if (!this.#open) return () => undefined;
    this.#pending += 1;
    let measured = false;
    return () => {
      if (measured) return;
      measured = true;
      this.#pending -= 1;
      this.#check();
    };
  }

  /**
   * The pane is hidden and every email drawn with it has been added: from now on the last one to
   * load shows it, or it is shown now if none is waited for. Before this nothing can show it, so an
   * email already loaded as it is drawn cannot close the wait before the pane has been hidden.
   */
  start(): void {
    this.#started = true;
    this.#check();
  }

  #check(): void {
    if (this.#started && this.#open && this.#pending === 0) this.#close();
  }

  /** The wait is over, measured or not. */
  expire(): void {
    if (this.#open) this.#close();
  }

  #close(): void {
    this.#open = false;
    this.#settled();
    for (const listener of this.#listeners) listener();
  }
}

const Settle = createContext<FrameSettle | null>(null);
export const FrameSettleProvider = Settle.Provider;

const noSubscription = (): (() => void) => () => undefined;
const alreadySettled = (): boolean => true;

/** Focus requests wait until the pane has actually been shown, including the expiry path. */
export function usePaneSettled(): boolean {
  const settle = useContext(Settle);
  return useSyncExternalStore(
    settle?.subscribe ?? noSubscription,
    settle?.isSettled ?? alreadySettled,
  );
}

/**
 * The pane `pane` holds conversation `id` hidden (`data-settling`) until its emails are measured.
 * Each email adds itself in a layout effect, which runs before this one, the parent's, and all of it
 * before the first paint: a conversation with no email, or with emails already measured, is never
 * drawn hidden.
 */
export function usePaneSettle(pane: React.RefObject<HTMLElement | null>, id: string): FrameSettle {
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new conversation is a new wait
  const settle = useMemo(
    () => new FrameSettle(() => pane.current?.removeAttribute("data-settling")),
    [pane, id],
  );
  useLayoutEffect(() => {
    pane.current?.setAttribute("data-settling", "");
    settle.start();
    const timer = setTimeout(() => settle.expire(), SETTLE_MS);
    return () => {
      clearTimeout(timer);
      settle.expire();
    };
  }, [pane, settle]);
  return settle;
}

/**
 * An email in a settling pane: added as it is drawn, done once its document has loaded and the
 * height measured then is on the page. Not before: measured while its document is still coming in,
 * every email reads the same small height (134 px each for a 1,484 px and a 74 px one, on a phone).
 * A layout effect, so the pane is shown in the same paint as the height, never a frame before it.
 */
export function useSettledAt(loaded: boolean): void {
  const settle = useContext(Settle);
  const done = useRef<(() => void) | null>(null);
  useLayoutEffect(() => {
    if (!settle) return;
    const measured = settle.add();
    done.current = measured;
    return () => {
      measured();
      done.current = null;
    };
  }, [settle]);
  useLayoutEffect(() => {
    if (!loaded) return;
    done.current?.();
    done.current = null;
  }, [loaded]);
}
